import { Component, h, Prop, Event, EventEmitter, State, Element } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { IconName } from '../icon/icons';
import { IconSize } from '../icon/icon';

@Component({
  tag: 'anchor-link-item',
  styleUrl: 'link-item.scss',
  shadow: true,
})
export class LinkItem {
  @Element() element: HTMLElement;

  /**
   *  Give this instance of link-item a custom ID, or keep the default.
   */
  @Prop({}) uuid?: string = uuidv4();

  /**
   * tabindex is a reserved name so we use tabi which will apply the tabindex attribute
   * Default value should work fine so don't change this unless you have a really good reason to. Set to -1 to disable tabbing.
   */
  @Prop({
    attribute: 'tab-index',
  })
  tabi?: number = 0;

  /**
   * Style of the link-item. You should ideally not mix these if using inside an link.
   */
  @Prop() variant?: 'default' | 'box' = 'default';

  /**
   * Text to display in the header
   */
  @Prop() label? = undefined;

  /**
   * Where should the user go, when clicking on the link-item.
   */
  @Prop() url? = undefined;

  /**
   * Icon to display in the header.
   * Note that the icon will be rotated when link-item is expanded.
   */
  @Prop() icon: IconName = 'chevron-right';

  /**
   * Size of the chevron.
   */
  @Prop() iconSize: IconSize = 'medium';

  /**
   * Stroke size for the chevron.
   */
  @Prop() iconStroke = 2;

  /**
   * Event dispatched when link-item expands or contracts
   * `{querySelecter}.addEventListener('toggled', ({detail}) => {console.log('expanded:', detail.value)})`
   */
  @Event() clicked: EventEmitter<{ value: boolean }>;

  private onClicked = () => {
    this.clicked.emit(this.url);
  };

  private onItemKeyUp(e: KeyboardEvent) {
    if (e.code === 'Space' || e.code === 'Enter') {
      this.onClicked();
    }
  }

  @State() isFocused: boolean;

  render() {
    return (
      <div
        class={{
          'anchor-c-link-item': true,
          'focused': this.isFocused,
          'box': this.variant === 'box',
        }}
      >
        <a
          href={this.url}
          class="link"
          onClick={() => this.onClicked()}
          onFocus={() => {
            this.isFocused = true;
          }}
          onBlur={() => {
            this.isFocused = false;
          }}
          onMouseDown={() =>
            setTimeout(() => {
              this.isFocused = false;
            }, 1)
          }
          onKeyUp={e => this.onItemKeyUp(e)}
          tabindex={this.tabi}
        >
          <div class="link-content">
            {this.label ? <span>{this.label}</span> : <slot name="label" />}
            <slot name="action" />
          </div>
          <anchor-icon name={this.icon} size={this.iconSize} stroke={this.iconStroke} />
        </a>
      </div>
    );
  }
}
